import { makeStyles } from "@mui/styles";
import { colors } from "../../styles/colors";
import { down, hoverOnly } from "../../styles/breakpoints";
import { pageShellRoot, pageContentRootWide, fullBleedOnMobile } from "../../styles/pageShell";

const useStyles = makeStyles((_) => ({
  contentMain: pageShellRoot,

  /* No width here on purpose: this class lands on <main>, which carries an
     inline margin-left for the sidebar. A declared width resolves against the
     full viewport and the margin is added on top, pushing the page's right
     edge into the shell's `overflow: hidden` where it is clipped away. */
  billingRoot: pageContentRootWide,

  /*
   * Responsive is not only about small screens. With the sidebar collapsed on
   * a 1920px display the settings rows stretched to ~1800px, which left each
   * toggle stranded roughly 1700px to the right of the label it belongs to --
   * the control and its meaning were no longer visually associated at all,
   * and the eye had to travel the full width of the monitor to pair them.
   *
   * Capping the measure keeps forms, cards and tables at a width the eye can
   * actually scan. The cap is above every common laptop width, so it only
   * engages on the large displays where the stretching was the problem.
   */
  contentWrapper: {
    width: "100%",
    maxWidth: "1400px",
    minWidth: 0,
    boxSizing: "border-box",
  },

  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "24px",
    gap: "16px",
    [down.md]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: "20px",
    },
    [down.sm]: { marginBottom: "16px", gap: "12px" },
  },

  divIconArrow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: "1 1 auto",
    minWidth: 0,
    "& h2": {
      color: colors.BLACK,
      fontSize: "clamp(20px, 2.2vw + 12px, 28px)",
      fontWeight: 700,
      margin: 0,
      letterSpacing: "0.5px",
      minWidth: 0,
      overflowWrap: "anywhere",
    },
  },

  iconCircle: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    backgroundColor: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(8, 152, 255, 0.3)",
    flexShrink: 0,
    [down.md]: { width: "48px", height: "48px", borderRadius: "10px" },
    [down.sm]: { width: "44px", height: "44px", borderRadius: "8px" },
  },

  tabs: {
    marginBottom: "24px",
    borderBottom: `1px solid ${colors.default}`,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "14px",
      minHeight: 48,
      color: colors.greydark,
      "&.Mui-selected": { color: colors.primary, fontWeight: 600 },
    },
    "& .MuiTabs-indicator": { height: 3, backgroundColor: colors.primary },
    [down.sm]: { marginBottom: "16px" },
  },

  // ── Cards ──────────────────────────────────────────────────────────

  card: {
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "20px",
    minWidth: 0,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${colors.default}`,
    [down.sm]: { padding: "16px", borderRadius: "8px" },
  },

  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "6px",
    "& h3": {
      margin: 0,
      fontSize: "16px",
      fontWeight: 600,
      color: colors.BLACK,
    },
  },

  cardHint: {
    margin: "0 0 20px 0",
    fontSize: "13px",
    lineHeight: 1.5,
    color: colors.greydark,
  },

  /** A single setting: label + description on the left, control on the right. */
  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "16px 0",
    borderBottom: `1px solid ${colors.default}`,
    "&:last-child": { borderBottom: "none" },
    [down.sm]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "10px",
    },
  },

  settingLabel: {
    minWidth: 0,
    /* A description running the full width of a wide card is hard to read:
       the eye loses the line on the way back. ~70 characters is the usual
       comfortable measure. */
    maxWidth: "70ch",
    "& strong": {
      display: "block",
      fontSize: "14px",
      fontWeight: 600,
      color: colors.BLACK,
      marginBottom: "2px",
    },
    "& span": {
      fontSize: "12.5px",
      lineHeight: 1.5,
      color: colors.greydark,
    },
  },

  settingControl: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    [down.sm]: { justifyContent: "flex-start" },
  },

  /** Disabled-looking wrapper for the trial-days field when trials are off. */
  dimmed: {
    opacity: 0.45,
    pointerEvents: "none",
  },

  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
    flexWrap: "wrap",
    [down.sm]: {
      flexDirection: "column-reverse",
      "& button": { width: "100%" },
    },
  },

  // ── Tables ─────────────────────────────────────────────────────────

  tableContainer: {
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${colors.default}`,
    minWidth: 0,
    "& .MuiTable-root": { minWidth: "760px" },
    "& thead": {
      backgroundColor: colors.bgcolor,
      "& th": {
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: colors.BLACK,
        whiteSpace: "nowrap",
        borderBottom: `2px solid ${colors.default}`,
        padding: "12px 16px",
      },
    },
    "& tbody td": {
      fontSize: "14px",
      color: colors.BLACK,
      borderBottom: `1px solid ${colors.default}`,
      padding: "12px 16px",
    },
    [down.sm]: fullBleedOnMobile,
  },

  filterBar: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "20px",
    padding: "16px 20px",
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${colors.default}`,
    [down.sm]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "10px",
      padding: "12px",
      borderRadius: "8px",
    },
  },

  filterControl: {
    minWidth: "200px",
    flex: "1 1 200px",
    maxWidth: "300px",
    [down.sm]: { minWidth: 0, width: "100%", maxWidth: "none" },
  },

  codeCell: {
    fontFamily: "source-code-pro, Menlo, Monaco, Consolas, monospace",
    fontWeight: 700,
    letterSpacing: "0.5px",
  },

  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    color: colors.greydark,
    fontSize: "14px",
  },

  // ── Pricing ────────────────────────────────────────────────────────

  planGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
    gap: "16px",
  },

  planCard: {
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    padding: "20px",
    minWidth: 0,
    border: `1px solid ${colors.default}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  planCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    minWidth: 0,
  },

  planName: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 700,
    color: colors.BLACK,
    minWidth: 0,
    overflowWrap: "anywhere",
  },

  planSlug: {
    fontSize: "11px",
    color: colors.greydark,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  priceRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "10px 0",
    borderTop: `1px solid ${colors.default}`,
    flexWrap: "wrap",
  },

  priceValue: {
    fontSize: "18px",
    fontWeight: 700,
    color: colors.BLACK,
  },

  priceCycle: {
    fontSize: "12px",
    color: colors.greydark,
  },

  /** Unit hint under a price field, e.g. "€ / véhicule / mois (× 12)". */
  priceHint: {
    fontSize: "11px",
    color: colors.greydark,
    opacity: 0.8,
    marginTop: "2px",
  },

  /** Warning shown above the price form about grandfathered subscribers. */
  warningBox: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    padding: "12px 14px",
    borderRadius: "8px",
    backgroundColor: colors.orangeLight,
    border: `1px solid ${colors.orangeDark}`,
    color: "#7a4a1f",
    fontSize: "13px",
    lineHeight: 1.5,
    marginBottom: "16px",
  },

  infoBox: {
    padding: "12px 14px",
    borderRadius: "8px",
    backgroundColor: colors.lightBlue,
    border: `1px solid ${colors.darkBlue}`,
    color: "#0b4f80",
    fontSize: "13px",
    lineHeight: 1.5,
    marginBottom: "16px",
  },

  // ── Dialog ─────────────────────────────────────────────────────────

  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "12px",
      width: "100%",
      maxWidth: "560px",
    },
    /* MUI keeps a 32px margin plus a rounded card on every viewport, so on a
       360px phone the promo form was squeezed into ~296px and its two-column
       rows had nowhere to go. Near-full-bleed below sm gives the fields
       their width back. */
    [down.sm]: {
      "& .MuiDialog-paper": {
        margin: 8,
        width: "calc(100% - 16px)",
        maxWidth: "none",
        maxHeight: "calc(100% - 16px)",
        borderRadius: 8,
      },
    },
  },

  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingTop: "8px !important",
  },

  formRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    "& > *": { flex: "1 1 200px", minWidth: 0 },
    [down.sm]: {
      flexDirection: "column",
      "& > *": { flex: "1 1 100%" },
    },
  },

  dialogActions: {
    padding: "16px 24px",
    gap: "12px",
    borderTop: `1px solid ${colors.default}`,
    [down.sm]: {
      flexDirection: "column-reverse",
      "& button": { width: "100%" },
    },
  },

  deleteButton: {
    color: colors.red,
    [hoverOnly]: {
      "&:hover": { backgroundColor: "rgba(222, 68, 68, 0.06)" },
    },
  },

  // ── Plan feature editor ──────────────────────────────────────
  planTabs: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    margin: "16px 0 8px",
  },
  featureGroup: {
    marginTop: "20px",
    paddingTop: "12px",
    borderTop: "1px solid #eceff1",
  },
  featureGroupTitle: {
    margin: "0 0 4px",
    fontSize: "14px",
    fontWeight: 700,
  },
  featureList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "2px 16px",
    marginTop: "8px",
  },
  featureLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
  },
  inheritedBadge: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    padding: "1px 6px",
    borderRadius: "10px",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  featureActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #eceff1",
  },
}));

export default useStyles;
